import React from 'react';
import type { IntlShape } from 'react-intl';

import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { computeText } from '@/locales/utils';
import { theme } from '@/core/theme';
import type { ActionDropdownCellProps } from '@/components/atoms/Table/ActionDropdownCell/ActionDropdownCell';
import type { DropDownText } from '@/components/atoms/DropDown/DropDown.type';

type DropdownItem = {
    value: number;
    label: string;
    onClick: () => void;
};

type BuildBannerActionCellParams = {
    isActive: boolean;
    intl: IntlShape;
    onToggle: () => void;
    dropdownItems: DropdownItem[];
    isToggleDisabled?: boolean;
};

export const buildBannerActionCell = ({
    isActive,
    intl,
    onToggle,
    dropdownItems,
    isToggleDisabled = false,
}: BuildBannerActionCellParams): { type: 'actionDropdown'; value: ActionDropdownCellProps } => {
    const buttonColor = isToggleDisabled ? theme.savory.colors.neutral[300] : theme.savory.colors.neutral[500];

    return {
        type: 'actionDropdown' as const,
        value: {
            action: {
                children: (
                    <span
                        style={{
                            color: buttonColor,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <SVGIcon icon={isActive ? SVGIcons.EYE : SVGIcons.EYE_DISABLED} size={14} color={buttonColor} />
                        {computeText(
                            intl,
                            isActive ? 'promotionalBanner.action.deactivate' : 'promotionalBanner.action.activate',
                        )}
                    </span>
                ),
                buttonType: 'secondary',
                hasBorder: true,
                disabled: isToggleDisabled,
                style: {
                    backgroundColor: 'transparent',
                    borderColor: buttonColor,
                    ...(isToggleDisabled && { opacity: 0.5, cursor: 'not-allowed' }),
                },
                onClick: onToggle,
            },
            dropdown: {
                type: 'text',
                button: (
                    <div
                        style={{
                            border: `1px solid ${theme.savory.colors.neutral[500]}`,
                            borderRadius: theme.radius.main,
                            height: '40px',
                            width: '40px',
                            boxSizing: 'border-box' as const,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <SVGIcon icon={SVGIcons.MORE_VERTICAL} size={16} color={theme.savory.colors.neutral[500]} />
                    </div>
                ),
                isUnstyledButton: true,
                align: 'end',
                withPortal: true,
                items: dropdownItems,
            } as DropDownText<unknown>,
        },
    };
};
